import { Component, ComponentInterface, Element, h, Host, Listen, Prop, State } from '@stencil/core';
import { AvatarSize } from './avatar.types';
import { getInitials } from './avatar.utils';
import { transformTagNames } from '../../global/transform-tag-name';
import { isSlotted } from '../../utils/is-slotted';

/**
 * The Avatar is a graphical representation of a user, dealer or third party. This visualizes through a profile picture
 * or image, an icon, or a string with initials.
 *
 * @slot icon - Custom icon of the avatar
 * @slot image - Image of the avatar
 */
@Component({
  tag: 'wb-avatar',
  styleUrl: 'avatar.scss',
  shadow: true,
})
export class AvatarComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** Size of the avatar */
  @Prop({ reflect: true })
  public size?: AvatarSize = 'm';

  /** Profile's full name */
  @Prop({ reflect: true })
  public fullName?: string;

  /** Tab focus state */
  @Prop({ reflect: true })
  public disableFocus?: boolean = false;

  /** Allow disabling hover for styling */
  @Prop({ reflect: true })
  public disableHover?: boolean = false;

  @State()
  public isHighlighted: boolean = false;

  @State()
  public hasImage: boolean = false;

  @State()
  public hasCustomIcon: boolean = false;

  @Listen('focus')
  @Listen('mouseenter')
  public onMouseEnter(): void {
    this.isHighlighted = true;
  }

  @Listen('blur')
  @Listen('mouseleave')
  public onMouseLeave(): void {
    this.isHighlighted = false;
  }

  private TagNames = transformTagNames(this, 'wb-avatar', ['wb-icon', 'wb-text']);

  private handleImageSlotChange = (): void => {
    this.hasImage = isSlotted(this.el, 'image');
  };

  private handleIconSlotChange = (): void => {
    this.hasCustomIcon = isSlotted(this.el, 'icon');
  };

  public render(): React.JSX.Element {
    const { WbIcon, WbText } = this.TagNames;

    const initials = this.fullName ? getInitials(this.fullName) : '';
    const hasInitials = Boolean(initials) && !this.hasCustomIcon;
    const hasDefaultIcon = !hasInitials && !this.hasCustomIcon;

    return (
      <Host class={{ 'wb-avatar': true, highlighted: this.isHighlighted }} tabindex={this.disableFocus ? '-1' : '0'}>
        {hasDefaultIcon && <WbIcon name="bds/user/24" class="icon" />}
        {hasInitials && (
          <WbText class="initials" size="l" static strong>
            {initials}
          </WbText>
        )}
        <slot name="icon" onSlotchange={this.handleIconSlotChange} />
        {this.hasImage && <span class="overlay"></span>}
        <slot name="image" onSlotchange={this.handleImageSlotChange} />
      </Host>
    );
  }
}
